import { useMemo } from "react";
import { getPermissions } from "../../hooks/usePermissions";

const PAY_ICONS: Record<string, string> = {
  card:     "💳",
  kaspi:    "🟡",
  halyk:    "🟢",
  transfer: "🏦",
};

interface DealCardProps {
  deal: any;
  client?: { firstName?: string; lastName?: string; companyName?: string } | null;
  onEdit?: () => void;
  onDelete: (id: number) => void;
  isNew?: boolean;
}

export default function DealCard({ deal, client, onEdit, onDelete, isNew }: DealCardProps) {
  const perms = getPermissions();
  const canDelete = perms.canDeleteDeals || perms.canRequestDeleteDeals;
  const clientName = useMemo(() => {
    if (!client) return "Без клиента";
    return `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() || client.companyName || "Без клиента";
  }, [client]);

  const amount     = Number(deal.amount ?? 0);
  const prepayment = Number(deal.prepayment ?? 0);
  const remaining  = amount - prepayment;
  const fmt = (n: number) => new Intl.NumberFormat("ru-RU").format(n) + " ₸";

  return (
    <div
      onClick={onEdit}
      style={{
        background: isNew ? "rgba(99,102,241,0.08)" : "#131926",
        border: `1px solid ${isNew ? "rgba(99,102,241,0.5)" : "#1f2937"}`,
        borderRadius: 10,
        padding: "11px 13px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 7,
        position: "relative",
        transition: "border-color 0.2s, background 0.2s",
        boxShadow: isNew ? "0 0 0 1px rgba(99,102,241,0.3)" : "none",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "#374151"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = isNew ? "rgba(99,102,241,0.5)" : "#1f2937"; }}
    >
      {isNew && (
        <div style={{
          position: "absolute", top: -6, right: 8,
          background: "#6366f1", color: "#fff",
          fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
          letterSpacing: "0.05em",
        }}>NEW</div>
      )}

      {/* Title */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "#f9fafb", lineHeight: 1.3, paddingRight: 16 }}>
        {deal.title}
      </div>

      {/* Client + amount row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {clientName}
        </div>
        {amount > 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
            {prepayment > 0 ? (
              <>
                <div style={{ fontSize: 11, color: "#6b7280", textDecoration: "line-through" }}>{fmt(amount)}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: remaining > 0 ? "#f59e0b" : "#10b981" }}>
                  {remaining > 0 ? fmt(remaining) : "Оплачено"}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, fontWeight: 700, color: "#c084fc" }}>{fmt(amount)}</div>
            )}
          </div>
        )}
      </div>

      {/* Prepayment badge */}
      {prepayment > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", padding: "1px 7px", borderRadius: 10 }}>
            аванс {fmt(prepayment)}
          </span>
        </div>
      )}

      {/* Tags row */}
      <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
        {deal.number && (
          <span style={{ fontSize: 10, color: "#4b5563", fontWeight: 600 }}>{deal.number}</span>
        )}
        {(deal.dealDate || deal.createdAt) && (
          <span style={{ fontSize: 10, color: "#4b5563" }}>
            {new Date((deal.dealDate || deal.createdAt) + (deal.dealDate ? "T00:00:00" : "")).toLocaleDateString("ru-RU", { day: "2-digit", month: "short" })}
          </span>
        )}
        {deal.paymentMethod && PAY_ICONS[deal.paymentMethod] && (
          <span style={{ fontSize: 12 }} title={deal.paymentMethod}>{PAY_ICONS[deal.paymentMethod]}</span>
        )}
      </div>

      {deal.deleteRequestedAt && (
        <span title="Запрошено удаление" style={{ position: "absolute", top: 7, right: 30, fontSize: 11, color: "#f87171", fontWeight: 700 }}>🗑 запрос</span>
      )}
      {/* Delete */}
      {canDelete && (
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(deal.id); }}
        style={{
          position: "absolute", top: 7, right: 7,
          background: "none", border: "none", color: "#374151",
          fontSize: 15, cursor: "pointer", lineHeight: 1, padding: "1px 4px",
          borderRadius: 4, transition: "color 0.15s",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#ef4444"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#374151"; }}
      >×</button>
      )}
    </div>
  );
}
